import { useRouter } from "next/router";
import {
  Page,
  Layout,
  Card,
  Frame,
  ResourceItem,
  ResourceList,
  TextStyle,
  Icon,
  Badge,
  Text,
  Stack,
} from "@shopify/polaris";
import {
  ChecklistAlternateMajor,
  WholesaleMajor,
  ProductsMajor,
  CashDollarMajor,
  PaymentsMajor,
  TransferWithinShopifyMajor,
  AutomationMajor,
  KeyMajor,
  InventoryMajor,
  NotificationMajor,
  TextMajor,
  OrdersMinor,
  TextBlockMajor,
  CustomersMajor
} from "@shopify/polaris-icons";
import { useCallback, useEffect, useState } from "react";
import { useAxios } from "../../hooks/useAxios";
import { useLoopAuth } from "../../components/user-provider";
import { userPermissionAccessType, permission } from "../../utils/constants";
import AccessDenied from "../access-denied";

import { scrollToTop } from "../../utils/helpers";

const Settings = ({ shopOrigin }) => {
  const { validateUserPermission, hasFeatureAccess, hasModuleAccess, user } = useLoopAuth();
  const router = useRouter();
  const settingConfig = [
    {
      sectionHeading: "Store",
      sectionComponents: [
        {
          title: "General",
          description: "View and update billing and delivery method settings.",
          icon: <Icon source={ChecklistAlternateMajor} color="base" />,
          click: () => {
            router.push("/settings/general", "/settings/general", {
              shallow: true,
            });
          },
          disabled: !hasFeatureAccess("generalSettings")
        },
        {
          title: "Notifications",
          description:
            "Manage email notifications sent to you and your customers.",
          icon: <Icon source={NotificationMajor} color="base" />,
          click: () => {
            router.push("/settings/notifications", "/settings/notifications", {
              shallow: true,
            });
          },
          disabled: !hasFeatureAccess("notificationSettings")
        },
        {
          title: "Multilingual texts",
          description:
            "Configure texts for customer portal, bundles and other Loop content in multiple languages.",
          icon: <Icon source={TextMajor} color="base" />,
          click: () => {
            router.push("/settings/multilingual", "/settings/multilingual", {
              shallow: true,
            });
          },
          disabled: !hasFeatureAccess("multilingualTexts")
        },
        {
          title: "Pricing",
          description: "View and manage your Loop Subscriptions plan.",
          icon: <Icon source={CashDollarMajor} color="base" />,
          click: () => {
            router.push("/settings/pricing", "/settings/pricing", {
              shallow: true,
            });
          },
          disabled: !hasFeatureAccess("pricingSettings")
        },
      ],
      disabled: !hasSectionAccess("Store")
    },
    {
      sectionHeading: "Order and shipping",
      sectionComponents: [
        {
          title: "Auto-updates",
          description:
            "View and manage auto-update settings for product, shipping and order notes.",
          icon: <Icon source={AutomationMajor} color="base" />,
          click: () => {
            router.push("/settings/auto-updates", "/settings/auto-updates", {
              shallow: true,
            });
          },
          disabled: !hasFeatureAccess("autoUpdates")
        },
        {
          title: "Inventory",
          description:
            "Manage how subscription billing should be handled in case of no inventory.",
          icon: <Icon source={InventoryMajor} color="base" />,
          click: () => {
            router.push("/settings/inventory-settings");
          },
          disabled: !hasFeatureAccess("inventory")
        },
        {
          title: "Subscription tags",
          description: "Create and manage the subscription tag workflows.",
          icon: <Icon source={ProductsMajor} color="base" />,
          click: () => {
            router.push(
              "/settings/subscription-tags",
              "/settings/subscription-tags",
              { shallow: true }
            );
          },
          disabled: !hasFeatureAccess("subscriptionTags")
        },
        {
          title: "Subscription shipping rates",
          description:
            "View and manage shipping rates for subscription orders.",
          icon: <Icon source={WholesaleMajor} color="base" />,
          click: () => {
            router.push(
              "/settings/shipping-rates",
              "/settings/shipping-rates",
              {
                shallow: true,
              }
            );
          },
          disabled: !hasFeatureAccess("subscriptionShippingRates")
        },
        {
          title: "Order schedule preferences",
          description:
            "Configure settings to schedule orders based on either charge date or actual delivery date.",
          icon: <Icon source={OrdersMinor} color="base" />,
          click: () => {
            router.push(
              "/settings/order-schedule",
              "/settings/order-schedule",
              {
                shallow: true,
              }
            );
          },
          disabled: !hasFeatureAccess("orderSchedule")
        },
      ],
      disabled: !hasSectionAccess("Order and shipping")
    },
    {
      sectionHeading: "Admin",
      sectionComponents: [
        {
          title: "Account log",
          description:
            "Check a detailed log of user actions that happened in your Loop account.",
          icon: <Icon source={TextBlockMajor} color="base" />,
          click: () => {
            router.push("/settings/account-logs", "/settings/account-logs", {
              shallow: true,
            });
          },
          disabled: !validateUserPermission(permission.ACCOUNT_LOGS, userPermissionAccessType.READ)
        },
        {
          title: "Users & permissions",
          description:
            "View a list of users with access to loop admin and manage their permissions.",
          icon: <Icon source={CustomersMajor} color="base" />,
          click: () => {
            router.push("/settings/user-permissions", "/settings/user-permissions", {
              shallow: true,
            });
          },
          disabled: !validateUserPermission(permission.UPDATE_USER_PERMISSION, userPermissionAccessType.WRITE)
        },
        {
          title: "API tokens",
          description:
            "Generate and manage Loop API tokens to read, create and update Loop subscription data externally.",
          icon: <Icon source={KeyMajor} color="base" />,
          click: () => {
            router.push("/settings/api-tokens", "/settings/api-tokens", {
              shallow: true,
            });
          },
          disabled: !validateUserPermission(permission.MANAGE_API_TOKENS, userPermissionAccessType.READ)
        }
      ],
      disabled: !validateUserPermission(permission.ACCOUNT_LOGS, userPermissionAccessType.READ) && !validateUserPermission(permission.ACCOUNT_LOGS, userPermissionAccessType.READ)
    },
    {
      sectionHeading: "Payments",
      sectionComponents: [
        {
          title: "Migrate payment methods",
          description:
            "Migrate customers and payment methods from Stripe and other payment platforms.",
          icon: <Icon source={TransferWithinShopifyMajor} color="base" />,
          click: () => {
            router.push(
              "/settings/customer-migration",
              "/settings/customer-migration",
              { shallow: true }
            );
          },
          disabled: !hasFeatureAccess("migrateCustomerAndPaymentMethod")
        },
        {
          title: "PayPal express setup",
          description:
            "Read detailed instructions to setup PayPal Express for subscriptions.",
          icon: <Icon source={PaymentsMajor} color="base" />,
          click: () => {
            router.push("/settings/paypal", "/settings/paypal", {
              shallow: true,
            });
          },
          disabled: !hasFeatureAccess("paypalExpressSetup")
        },
      ],
      disabled: !hasSectionAccess("Payments")
    },
  ];
    const settings = [];
    let settingConfigValidateSectionPermission = settingConfig.filter(ele => (ele.disabled !== true));
    for(const data of settingConfigValidateSectionPermission) {
        data.sectionComponents = data.sectionComponents.filter(ele => ele.disabled !== true)
    }

  function hasSectionAccess(sectionName) {
    switch (sectionName) {
      case "Store":
        return (
          hasFeatureAccess("generalSettings") || hasFeatureAccess("notificationSettings")
          || hasFeatureAccess("multilingualTexts") || hasFeatureAccess("pricingSettings")
        )
      case "Order and shipping":
        return (
          hasFeatureAccess("autoUpdates") || hasFeatureAccess("subscriptionTags")
          || hasFeatureAccess("inventory") || hasFeatureAccess("subscriptionShippingRates")
          || hasFeatureAccess("orderSchedule")
        )
      case "Payments":
        return (hasFeatureAccess("paypalExpressSetup") || hasFeatureAccess("migrateCustomerAndPaymentMethod"))

      default:
        return true
    }
  }

  useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <>
    {   
    !hasModuleAccess("settings") ? <AccessDenied userEmail = {user.email}/> :
    <Page title="Settings">
      {settingConfigValidateSectionPermission.map((settings, index) => {
        return (
          <Stack vertical spacing="tight">
            <Text as="h5" variant="bodyLg">
              {settings.sectionHeading}
            </Text>
            <Card fullWidth>
              <ResourceList
                items={settings.sectionComponents}
                renderItem={(setting) => {
                  // const media = <Avatar customer size="medium" name={name} />;
                  return (
                    <ResourceItem
                      verticalAlignment="center"
                      media={setting.icon}
                      onClick={setting.click}
                    >
                      <h3>
                        <TextStyle variation="strong">
                          {setting.title}
                        </TextStyle>
                      </h3>
                      <div>{setting.description}</div>
                    </ResourceItem>
                  );
                }}
              ></ResourceList>
            </Card>
            <br/>
          </Stack>
        );
      })}
    </Page>
    }
    </>
  );
};

export default Settings;
